import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Euro, Package, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';

import { API } from '../../lib/api';

const STATUSES = [
  { value: 'pending', label: 'In attesa', class: 'bg-yellow-100 text-yellow-700' },
  { value: 'confirmed', label: 'Confermato', class: 'bg-blue-100 text-blue-700' },
  { value: 'delivered', label: 'Consegnato', class: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Annullato', class: 'bg-red-100 text-red-700' }
];

export default function AdminOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [orphanData, setOrphanData] = useState(null);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status aggiornato!');
      fetchOrders();
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const checkOrphanOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/orphans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrphanData(response.data);
      setOrphanDialogOpen(true);
    } catch (error) {
      toast.error('Errore nel controllo ordini orfani');
    }
  };

  const deleteOrphanOrders = async () => {
    setCleaningOrphans(true);
    try {
      const response = await axios.delete(`${API}/admin/orders/orphans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setOrphanDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error('Errore durante la pulizia');
    } finally {
      setCleaningOrphans(false);
    }
  };

  const deleteSingleOrder = async (orderId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ordine?')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ordine eliminato');
      fetchOrders();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const getStatusBadge = (status) => {
    const s = STATUSES.find(st => st.value === status) || STATUSES[0];
    return (
      <span className={`${s.class} px-3 py-1 rounded-full text-xs font-medium`}>
        {s.label}
      </span>
    );
  };

  return (
    <AdminLayout title="Gestione Ordini Shop">
      <div className="flex justify-between items-center mb-6">
        <p className="font-manrope text-[#4A5568]">
          {orders.length} ordini totali
        </p>
        <Button
          onClick={checkOrphanOrders}
          variant="outline"
          className="flex items-center gap-2 border-orange-300 text-orange-600 hover:bg-orange-50"
        >
          <AlertTriangle className="w-4 h-4" />
          Pulisci Ordini Orfani
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : orders.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="py-12 text-center">
            <ShoppingCart className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
            <p className="font-manrope text-[#4A5568]">Nessun ordine ricevuto.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} className="border-[#E2E8F0]">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-manrope text-sm text-[#4A5568] mb-1">
                      Ordine #{order.id.slice(0, 8)}
                    </p>
                    <p className="font-manrope text-xs text-[#4A5568]">
                      {new Date(order.created_at).toLocaleString('it-IT')}
                    </p>
                    {order.quote_id && !order.booking_id && (
                      <p className="font-manrope text-xs text-orange-500 mt-1">
                        ⚠️ Collegato a preventivo non accettato
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(order.status)}
                    <Select 
                      value={order.status} 
                      onValueChange={(v) => handleStatusChange(order.id, v)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => deleteSingleOrder(order.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Order Items */}
                <div className="bg-[#F9F9F7] rounded-sm p-4 mb-4">
                  <p className="font-manrope text-sm font-medium text-[#4A5568] mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Prodotti ordinati
                  </p>
                  <div className="space-y-2">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-[#1A202C]">
                          {item.nome} × {item.quantita || 1}
                        </span>
                        <span className="text-[#4A5568]">
                          €{(item.subtotale || item.totale || 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Note */}
                {order.note && (
                  <p className="text-sm text-[#4A5568] mb-4 italic">
                    Note: {order.note}
                  </p>
                )}

                {/* Total */}
                <div className="flex justify-between items-center pt-4 border-t border-[#E2E8F0]">
                  <span className="font-manrope font-medium">Totale</span>
                  <span className="font-cinzel text-xl text-[#C5A059] flex items-center gap-1">
                    <Euro className="w-4 h-4" />
                    {(order.totale || 0).toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Ordini Orfani */}
      <Dialog open={orphanDialogOpen} onOpenChange={setOrphanDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Ordini Orfani
            </DialogTitle>
            <DialogDescription>
              Ordini collegati a preventivi non accettati o eliminati.
            </DialogDescription>
          </DialogHeader>
          
          {orphanData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{orphanData.orphan_count}</p>
                  <p className="text-sm text-red-500">Ordini Orfani</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{orphanData.valid_count}</p>
                  <p className="text-sm text-green-500">Ordini Validi</p>
                </div>
              </div>

              {orphanData.orphan_orders?.length > 0 && (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  <p className="text-sm font-medium text-gray-700">Ordini da eliminare:</p>
                  {orphanData.orphan_orders.map((order, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="font-medium">#{order.id.slice(0, 8)}...</p>
                      <p className="text-gray-500 text-xs">{order.orphan_reason}</p>
                      <p className="text-gray-600">
                        {order.items?.map(i => i.nome).join(', ')}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setOrphanDialogOpen(false)}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={deleteOrphanOrders}
                  disabled={cleaningOrphans || orphanData.orphan_count === 0}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                >
                  {cleaningOrphans ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Elimina {orphanData.orphan_count} Orfani
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
